import { Suspense, useMemo, useState, useCallback, useRef } from 'react';
import { InView } from 'react-intersection-observer';
import { makeClassName } from '@siteless/util';
import Modal from './Modal';
import { Header } from './Header';
import ballotDisplayOrder from '../resources/Ballot/constants/ballotDisplayOrder';
import optionTypes from '../resources/Ballot/constants/optionTypes';
import ObjectiveStateResource from '../resources/ObjectiveState';
import useCopy from '../util/useCopy';
import LaurelLeftIcon from './LaurelLeftIcon';
import LaurelRightIcon from './LaurelRightIcon';
import ArrowUpRightIcon from './ArrowUpRightIcon';
import ClipboardIcon from './ClipboardIcon';
import { Tooltip } from './Tooltip';

const NomineeRow = ({
  title,
  details,
  imageUrl,
  href,
  nominationCount,
  nominations,
  filmIsNominee,
  filmTitle,
}: {
  title: string;
  details?: string;
  imageUrl?: string;
  href?: string;
  nominationCount?: number;
  nominations?: string[];
  filmIsNominee?: boolean;
  filmTitle?: string;
}) => {
  const nominationsText =
    nominationCount && nominationCount > 0
      ? filmIsNominee
        ? `${nominationCount} nomination${nominationCount > 1 ? 's' : ''}`
        : `${nominationCount} nomination${
            nominationCount > 1 ? 's' : ''
          } for ${filmTitle}`
      : '';
  const nominationsTooltip = nominations?.join('\n') || '';

  return (
    <div className="flex items-start gap-3 py-2">
      <div className="h-14 w-10 flex-none overflow-hidden rounded">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={title}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-neutral-100 p-1 text-center text-[7px] text-neutral-400">
            {title}
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          {href ? (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium"
            >
              {title}
            </a>
          ) : (
            <div className="font-medium">{title}</div>
          )}
          {href && (
            <ArrowUpRightIcon className="h-3 w-3 flex-none text-neutral-400" />
          )}
        </div>
        {nominationsText && (
          <Tooltip
            text={nominationsTooltip}
            tooltipSide="bottom"
            className="cursor-pointer"
          >
            <span className="truncate block text-xs text-neutral-500 underline decoration-dotted">
              {nominationsText}
            </span>
          </Tooltip>
        )}
        {details && (
          <Tooltip
            text={details}
            tooltipSide="bottom"
            className="cursor-pointer"
          >
            <div className="line-clamp-2 text-sm text-neutral-500">
              {details}
            </div>
          </Tooltip>
        )}
      </div>
    </div>
  );
};

const CategorySection = ({
  categoryKey,
  nominationsByFilm,
}: {
  categoryKey: (typeof ballotDisplayOrder)[number];
  nominationsByFilm: Map<string, string[]>;
}) => {
  const copy = useCopy();
  const objectiveState = ObjectiveStateResource.use();

  const { displayName, options } = objectiveState.ballotShape[categoryKey];

  const winnerOptions = Object.entries(options).filter(
    ([, option]) => option.type === 'winner',
  );

  return (
    <div>
      <div className="px-4 py-3">
        <div className="font-medium">{displayName}</div>
      </div>
      <div className="grid grid-cols-2 gap-x-4 px-4 pb-3">
        {winnerOptions.map(([optionKey, option]) => {
          if (option.type !== 'winner') return null;

          const props = optionTypes.winner.getOptionComponentProps({
            data: option.data,
            copy,
            objectiveState,
          });

          const imageUrl = props.supportImageUrl || props.posterImageUrl;

          const category =
            objectiveState.nomineeCategories[option.data.categoryKey];
          const nomineeData = category?.nominees[
            option.data.nomineeId as keyof typeof category.nominees
          ] as { filmId: keyof typeof objectiveState.films } | undefined;
          const filmId = nomineeData?.filmId;
          const film = filmId ? objectiveState.films[filmId] : null;
          const tmdbId =
            film && 'tmdb_data' in film ? film.tmdb_data.tmdb_id : undefined;
          const href = tmdbId
            ? `https://letterboxd.com/tmdb/${tmdbId}/`
            : undefined;

          const nominations = filmId ? nominationsByFilm.get(filmId) || [] : [];

          return (
            <NomineeRow
              key={optionKey}
              title={props.title}
              details={props.details}
              imageUrl={imageUrl}
              href={href}
              nominationCount={nominations.length}
              nominations={nominations}
              filmIsNominee={props.filmIsNominee}
              filmTitle={film?.title}
            />
          );
        })}
      </div>
    </div>
  );
};

const NomineesModalContent = ({
  showFinishBallot,
  onFinishBallot,
}: {
  showFinishBallot: boolean;
  onFinishBallot: () => void;
}) => {
  const copy = useCopy();
  const objectiveState = ObjectiveStateResource.use();

  const [visibleCategories, setVisibleCategories] = useState<Set<string>>(
    new Set(),
  );
  const navPillRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const categorySectionRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const ensureNavItemVisible = useCallback((categoryKey: string) => {
    const pill = navPillRefs.current.get(categoryKey);
    pill?.scrollIntoView({
      behavior: 'smooth',
      inline: 'nearest',
      block: 'nearest',
    });
  }, []);

  const scrollToCategory = useCallback((categoryKey: string) => {
    const section = categorySectionRefs.current.get(categoryKey);
    section?.scrollIntoView({
      behavior: 'instant',
      block: 'center',
    });
  }, []);

  const nominationsByFilm = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const [, category] of Object.entries(
      objectiveState.nomineeCategories,
    )) {
      for (const nominee of Object.values(category.nominees)) {
        const filmId = (nominee as { filmId: string }).filmId;
        if (!map.has(filmId)) {
          map.set(filmId, []);
        }
        map.get(filmId)!.push(category.title);
      }
    }
    return map;
  }, [objectiveState.nomineeCategories]);

  const categoryKeys = ballotDisplayOrder.filter((key) => {
    if (key === 'thankYou') return false;
    const category = objectiveState.ballotShape[key];
    if (!category) return false;
    return Object.values(category.options).some((opt) => opt.type === 'winner');
  });

  const handleVisibilityChange = useCallback(
    (categoryKey: string, inView: boolean) => {
      setVisibleCategories((prev) => {
        const hasKey = prev.has(categoryKey);
        if (inView && hasKey) return prev;
        if (!inView && !hasKey) return prev;
        const next = new Set(prev);
        if (inView) {
          next.add(categoryKey);
        } else {
          next.delete(categoryKey);
        }
        return next;
      });
      if (inView) {
        ensureNavItemVisible(categoryKey);
      }
    },
    [ensureNavItemVisible],
  );

  return (
    <div className="relative flex-1 overflow-hidden">
      <div className="h-full overflow-auto">
        <div className="flex flex-col items-center gap-2 border-b border-neutral-100 px-4 pb-6 pt-8">
          <div className="flex items-center gap-2">
            <LaurelLeftIcon className="h-8 w-8 text-neutral-300" />
            <Header>{copy.Nominees}</Header>
            <LaurelRightIcon className="h-8 w-8 text-neutral-300" />
          </div>
        </div>
        <div className="pb-24">
          {categoryKeys.map((categoryKey) => (
            <div
              key={categoryKey}
              ref={(el) => {
                if (el) categorySectionRefs.current.set(categoryKey, el);
                else categorySectionRefs.current.delete(categoryKey);
              }}
              className="border-b border-neutral-100 last:border-b-0"
            >
              <InView
                threshold={0.8}
                onChange={(inView) =>
                  handleVisibilityChange(categoryKey, inView)
                }
              >
                <CategorySection
                  categoryKey={categoryKey}
                  nominationsByFilm={nominationsByFilm}
                />
              </InView>
            </div>
          ))}
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 flex flex-col items-center gap-2 pb-safe-offset-3 px-3">
        {showFinishBallot && (
          <button
            onClick={onFinishBallot}
            className="flex items-center gap-2 rounded-full border-4 border-rose-dark bg-rose-default px-4 py-2 text-sm font-medium text-neutral-0 shadow-lg"
          >
            <ClipboardIcon className="h-4 w-4" />
            {copy.finishYourBallot}
          </button>
        )}
        <div className="relative w-full overflow-hidden rounded-full border-4 border-neutral-200 bg-neutral-0 shadow-lg">
          <div className="pointer-events-none absolute left-0 top-0 bottom-0 z-10 w-3 bg-gradient-to-r from-neutral-0 to-transparent" />
          <div className="pointer-events-none absolute right-0 top-0 bottom-0 z-10 w-3 bg-gradient-to-l from-neutral-0 to-transparent" />
          <div className="flex overflow-x-auto px-1 py-1">
            {categoryKeys.map((categoryKey, index) => {
              const isVisible = visibleCategories.has(categoryKey);
              const prevVisible =
                index > 0 && visibleCategories.has(categoryKeys[index - 1]);
              const nextVisible =
                index < categoryKeys.length - 1 &&
                visibleCategories.has(categoryKeys[index + 1]);
              const displayName =
                objectiveState.ballotShape[categoryKey]?.displayName ||
                categoryKey;

              return (
                <div
                  key={categoryKey}
                  ref={(el) => {
                    if (el) navPillRefs.current.set(categoryKey, el);
                    else navPillRefs.current.delete(categoryKey);
                  }}
                  onClick={() => scrollToCategory(categoryKey)}
                  className={makeClassName(
                    'cursor-pointer whitespace-nowrap px-2.5 py-1 text-sm font-medium mx-0.5',
                    isVisible && 'bg-neutral-200 text-neutral-800',
                    isVisible && !prevVisible && 'rounded-l-full',
                    isVisible && !nextVisible && 'rounded-r-full',
                    isVisible && nextVisible && 'shadow-[4px_0_0_0_#e5e7eb]',
                    !isVisible && 'rounded-full text-neutral-500',
                  )}
                >
                  {displayName}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

const NomineesModal = ({
  isOpen,
  onClose,
  showFinishBallot,
  onFinishBallot,
}: {
  isOpen: boolean;
  onClose: () => void;
  showFinishBallot: boolean;
  onFinishBallot: () => void;
}) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} disableBodyPadding>
      <Suspense fallback={<Modal.Loader />}>
        <NomineesModalContent
          showFinishBallot={showFinishBallot}
          onFinishBallot={onFinishBallot}
        />
      </Suspense>
    </Modal>
  );
};

export default NomineesModal;
